import React, { useState, useEffect } from 'react';
import {
    Box, Tabs, Tab, Typography, TextField, Button,
    Table, TableBody, TableCell, TableHead, TableRow,
    Paper, Select, MenuItem, FormControl, InputLabel,
    Autocomplete, Card, CardContent, CardHeader, Grid,
    Container, Divider, IconButton, TableContainer,
    Tooltip, CircularProgress
} from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import Loading from '../components/Loading';
import API_URL from '../config';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function Setup() {
    const [value, setValue] = useState(0);
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [countries, setCountries] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [ticketTypes, setTicketTypes] = useState([]);
    const [loading, setLoading] = useState(true);

    // Form States
    const [newUser, setNewUser] = useState({ username: '', password: '', full_name: '', role_id: '', country_ids: [], division_ids: [], manager_id: '' });
    const [editingUserId, setEditingUserId] = useState(null);

    const [newRole, setNewRole] = useState('');
    const [newCountry, setNewCountry] = useState({ name: '', code: '' });
    const [newDivision, setNewDivision] = useState({ name: '', code: '' });
    const [newTicketType, setNewTicketType] = useState('');

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };


    // AD Search
    const [adUsers, setAdUsers] = useState([]);
    const [adLoading, setAdLoading] = useState(false);

    const handleADSearch = async (query) => {
        if (!query || query.length < 3) {
            setAdUsers([]);
            return;
        }
        setAdLoading(true);
        try {
            const res = await axios.get(`${API_URL}/tickets/search-ad-users?q=${query}`, { headers });
            setAdUsers(res.data);
        } catch (e) {
            console.error(e);
        } finally {
            setAdLoading(false);
        }
    };

    // ... existing fetch logic ...

    const fetchData = async () => {
        setLoading(true);
        const fetchEndpoint = async (url, setter) => {
            try {
                const res = await axios.get(url, { headers });
                setter(res.data);
            } catch (err) {
                console.error(`Error fetching ${url}:`, err);
                if (err.response && err.response.status === 401) {
                    // Token might be expired
                    alert("Session expired. Please logout and login again.");
                }
            }
        };

        await Promise.all([
            fetchEndpoint(`${API_URL}/setup/users`, setUsers),
            fetchEndpoint(`${API_URL}/setup/roles`, setRoles),
            fetchEndpoint(`${API_URL}/setup/countries`, setCountries),
            fetchEndpoint(`${API_URL}/setup/divisions`, setDivisions),
            fetchEndpoint(`${API_URL}/ticket-types`, setTicketTypes)
        ]);
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleCreateUser = async () => {
        try {
            await axios.post(`${API_URL}/setup/users`, newUser, { headers });
            fetchData();
            setNewUser({ username: '', password: '', full_name: '', role_id: '', country_ids: [], division_ids: [], manager_id: '' });
        } catch (err) {
            alert('Error creating user');
        }
    };

    const handleEditUser = (user) => {
        setEditingUserId(user.id);
        setNewUser({
            username: user.username,
            password: '', // Leave empty to not change
            full_name: user.full_name,
            role_id: user.role_id || '',
            country_ids: user.country_ids || [],
            division_ids: user.division_ids || [],
            manager_id: user.manager_id || ''
        });
    };

    const handleUpdateUser = async () => {
        try {
            await axios.put(`${API_URL}/setup/users/${editingUserId}`, newUser, { headers });
            fetchData();
            handleCancelEdit();
        } catch (err) {
            alert(err.response?.data?.message || 'Error updating user');
        }
    };

    const handleDeleteUser = async (id) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        try {
            await axios.delete(`${API_URL}/setup/users/${id}`, { headers });
            fetchData();
        } catch (err) {
            alert(err.response?.data?.message || 'Error deleting user');
        }
    };

    const handleCancelEdit = () => {
        setEditingUserId(null);
        setNewUser({ username: '', password: '', full_name: '', role_id: '', country_ids: [], division_ids: [], manager_id: '' });
    };

    const handleCreateRole = async () => {
        try {
            await axios.post(`${API_URL}/setup/roles`, { name: newRole }, { headers });
            fetchData();
            setNewRole('');
        } catch (err) {
            alert(err.response?.data?.message || 'Error creating role');
        }
    };

    const handleCreateCountry = async () => {
        try {
            await axios.post(`${API_URL}/setup/countries`, newCountry, { headers });
            fetchData();
            setNewCountry({ name: '', code: '' });
        } catch (err) {
            alert(err.response?.data?.message || 'Error creating country');
        }
    };

    const handleCreateDivision = async () => {
        try {
            await axios.post(`${API_URL}/setup/divisions`, newDivision, { headers });
            fetchData();
            setNewDivision({ name: '', code: '' });
        } catch (err) {
            alert(err.response?.data?.message || 'Error creating division');
        }
    };

    const handleCreateTicketType = async () => {
        try {
            await axios.post(`${API_URL}/ticket-types`, { name: newTicketType }, { headers });
            fetchData();
            setNewTicketType('');
        } catch (err) {
            alert(err.response?.data?.message || 'Error creating ticket type');
        }
    };

    const handleDeleteTicketType = async (id) => {
        if (!window.confirm('Are you sure?')) return;
        try {
            await axios.delete(`${API_URL}/ticket-types/${id}`, { headers });
            fetchData();
        } catch (err) {
            alert('Error deleting ticket type');
        }
    };

    if (loading) return <Loading message="Loading Setup Data..." />;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight="bold" sx={{ background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    System Configuration
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Manage users, roles, and structural references
                </Typography>
            </Box>

            <Paper sx={{ width: '100%', mb: 2 }}>
                <Tabs
                    value={value}
                    onChange={(e, v) => setValue(v)}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab label="Users Management" />
                    <Tab label="Roles" />
                    <Tab label="Countries" />
                    <Tab label="Divisions" />
                    <Tab label="Ticket Types" />
                </Tabs>

                {/* Users Tab */}
                <TabPanel value={value} index={0}>
                    <Grid container spacing={3}>
                        {/* Create/Edit User Form */}
                        <Grid item xs={12}>
                            <Card variant="outlined">
                                <CardHeader
                                    title={editingUserId ? "Edit User" : "Create New User"}
                                    titleTypographyProps={{ variant: 'h6' }}
                                    action={
                                        editingUserId && (
                                            <Button startIcon={<CancelIcon />} onClick={handleCancelEdit} color="inherit">
                                                Cancel Edit
                                            </Button>
                                        )
                                    }
                                />
                                <Divider />
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Autocomplete
                                                freeSolo
                                                fullWidth
                                                size="small"
                                                options={adUsers}
                                                loading={adLoading}
                                                getOptionLabel={(option) => {
                                                    if (typeof option === 'string') return option;
                                                    return option.displayName || option.name || '';
                                                }}
                                                onInputChange={(event, newInputValue) => {
                                                    handleADSearch(newInputValue);
                                                }}
                                                renderOption={(props, option) => (
                                                    <li {...props}>
                                                        <Box>
                                                            <Typography variant="body2">{option.displayName}</Typography>
                                                            <Typography variant="caption" color="textSecondary" display="block">{option.mail || option.userPrincipalName}</Typography>
                                                        </Box>
                                                    </li>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Username / Search AD"
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            endAdornment: (
                                                                <React.Fragment>
                                                                    {adLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                                    {params.InputProps.endAdornment}
                                                                </React.Fragment>
                                                            )
                                                        }}
                                                    />
                                                )}
                                                value={newUser.username}
                                                onChange={(event, newValue) => {
                                                    if (typeof newValue === 'string') {
                                                        setNewUser({ ...newUser, username: newValue });
                                                    } else if (newValue) {
                                                        const username = newValue.userPrincipalName || newValue.mail;
                                                        const updates = {
                                                            ...newUser,
                                                            username: username,
                                                            full_name: newValue.displayName
                                                        };

                                                        // Try to match country
                                                        if (newValue.country) {
                                                            const cObj = countries.find(c => c.name === newValue.country);
                                                            if (cObj) {
                                                                // Add to country_ids if not exists
                                                                if (!newUser.country_ids.includes(cObj.id)) {
                                                                    updates.country_ids = [cObj.id];
                                                                }
                                                            }
                                                        }
                                                        setNewUser(updates);
                                                    } else {
                                                        setNewUser({ ...newUser, username: '' });
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                fullWidth
                                                label={editingUserId ? "Password (leave blank to keep)" : "Password"}
                                                type="password"
                                                size="small"
                                                value={newUser.password}
                                                onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField fullWidth label="Full Name" size="small" value={newUser.full_name} onChange={e => setNewUser({ ...newUser, full_name: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Role</InputLabel>
                                                <Select value={newUser.role_id} label="Role" onChange={e => setNewUser({ ...newUser, role_id: e.target.value })}>
                                                    {roles.filter(r => !['Country Manager', 'Division Manager'].includes(r.name)).map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Autocomplete
                                                multiple
                                                fullWidth
                                                size="small"
                                                options={countries}
                                                getOptionLabel={(option) => option.name}
                                                value={countries.filter(c => newUser.country_ids.includes(c.id))}
                                                onChange={(event, newValue) => {
                                                    setNewUser({ ...newUser, country_ids: newValue.map(c => c.id) });
                                                }}
                                                renderInput={(params) => <TextField {...params} label="Countries" />}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Autocomplete
                                                multiple
                                                fullWidth
                                                size="small"
                                                options={divisions}
                                                getOptionLabel={(option) => option.name}
                                                value={divisions.filter(d => newUser.division_ids.includes(d.id))}
                                                onChange={(event, newValue) => {
                                                    setNewUser({ ...newUser, division_ids: newValue.map(d => d.id) });
                                                }}
                                                renderInput={(params) => <TextField {...params} label="Divisions" />}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Autocomplete
                                                fullWidth
                                                size="small"
                                                options={users}
                                                getOptionLabel={(option) => option.full_name || ''}
                                                value={users.find(u => u.id === newUser.manager_id) || null}
                                                onChange={(event, newValue) => {
                                                    setNewUser({ ...newUser, manager_id: newValue ? newValue.id : '' });
                                                }}
                                                renderInput={(params) => <TextField {...params} label="Manager" />}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3} display="flex" alignItems="center">
                                            {editingUserId ? (
                                                <Button fullWidth variant="contained" color="warning" startIcon={<SaveIcon />} onClick={handleUpdateUser}>Update User</Button>
                                            ) : (
                                                <Button fullWidth variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={handleCreateUser}>Create User</Button>
                                            )}
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Users Table */}
                        <Grid item xs={12}>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: 'grey.50' }}>
                                        <TableRow>
                                            <TableCell>Username</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Role</TableCell>
                                            <TableCell>Manager</TableCell>
                                            <TableCell>Countries</TableCell>
                                            <TableCell>Divisions</TableCell>
                                            <TableCell>Fingerprint</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {users.map(u => (
                                            <TableRow key={u.id} hover selected={editingUserId === u.id}>
                                                <TableCell>{u.username}</TableCell>
                                                <TableCell sx={{ fontWeight: 'medium' }}>{u.full_name}</TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', px: 1, py: 0.5, borderRadius: 1 }}>
                                                        {u.role}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{users.find(m => m.id === u.manager_id)?.full_name || '-'}</TableCell>
                                                <TableCell>
                                                    {u.countries && u.countries.length > 0
                                                        ? u.countries.map(c => c.name).join(', ')
                                                        : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {u.divisions && u.divisions.length > 0
                                                        ? u.divisions.map(d => d.name).join(', ')
                                                        : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {u.has_fingerprint ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                                                            <FingerprintIcon fontSize="small" sx={{ mr: 0.5 }} />
                                                            Registered
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="caption" color="text.disabled">Not Registered</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" color="primary" onClick={() => handleEditUser(u)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" color="error" onClick={() => handleDeleteUser(u.id)}>
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Roles Tab */}
                <TabPanel value={value} index={1}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Card variant="outlined">
                                <CardHeader title="Add Role" titleTypographyProps={{ variant: 'subtitle1' }} />
                                <Divider />
                                <CardContent sx={{ display: 'flex', gap: 1 }}>
                                    <TextField fullWidth size="small" label="Role Name" value={newRole} onChange={e => setNewRole(e.target.value)} />
                                    <Button variant="contained" onClick={handleCreateRole}>Add</Button>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: 'grey.50' }}><TableRow><TableCell>ID</TableCell><TableCell>Name</TableCell></TableRow></TableHead>
                                    <TableBody>{roles.map(r => <TableRow key={r.id}><TableCell>{r.id}</TableCell><TableCell>{r.name}</TableCell></TableRow>)}</TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Countries Tab */}
                <TabPanel value={value} index={2}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={5}>
                            <Card variant="outlined">
                                <CardHeader title="Add Country" titleTypographyProps={{ variant: 'subtitle1' }} />
                                <Divider />
                                <CardContent>
                                    <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
                                        <TextField fullWidth size="small" label="Country Name" value={newCountry.name} onChange={e => setNewCountry({ ...newCountry, name: e.target.value })} />
                                        <TextField fullWidth size="small" label="Code (1 Letter)" value={newCountry.code} onChange={e => setNewCountry({ ...newCountry, code: e.target.value })} inputProps={{ maxLength: 1 }} />
                                        <Button fullWidth variant="contained" onClick={handleCreateCountry}>Add Country</Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: 'grey.50' }}><TableRow><TableCell>ID</TableCell><TableCell>Name</TableCell><TableCell>Code</TableCell></TableRow></TableHead>
                                    <TableBody>{countries.map(c => <TableRow key={c.id}><TableCell>{c.id}</TableCell><TableCell>{c.name}</TableCell><TableCell>{c.code}</TableCell></TableRow>)}</TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Divisions Tab */}
                <TabPanel value={value} index={3}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={5}>
                            <Card variant="outlined">
                                <CardHeader title="Add Division" titleTypographyProps={{ variant: 'subtitle1' }} />
                                <Divider />
                                <CardContent>
                                    <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
                                        <TextField fullWidth size="small" label="Division Name" value={newDivision.name} onChange={e => setNewDivision({ ...newDivision, name: e.target.value })} />
                                        <TextField fullWidth size="small" label="Code (1 Letter)" value={newDivision.code} onChange={e => setNewDivision({ ...newDivision, code: e.target.value })} inputProps={{ maxLength: 1 }} />
                                        <Button fullWidth variant="contained" onClick={handleCreateDivision}>Add Division</Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: 'grey.50' }}><TableRow><TableCell>ID</TableCell><TableCell>Name</TableCell><TableCell>Code</TableCell></TableRow></TableHead>
                                    <TableBody>{divisions.map(d => <TableRow key={d.id}><TableCell>{d.id}</TableCell><TableCell>{d.name}</TableCell><TableCell>{d.code}</TableCell></TableRow>)}</TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Ticket Types Tab */}
                <TabPanel value={value} index={4}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Card variant="outlined">
                                <CardHeader title="Add Ticket Type" titleTypographyProps={{ variant: 'subtitle1' }} />
                                <Divider />
                                <CardContent sx={{ display: 'flex', gap: 1 }}>
                                    <TextField fullWidth size="small" label="Type Name" value={newTicketType} onChange={e => setNewTicketType(e.target.value)} />
                                    <Button variant="contained" onClick={handleCreateTicketType}>Add</Button>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: 'grey.50' }}><TableRow><TableCell>ID</TableCell><TableCell>Name</TableCell><TableCell>Action</TableCell></TableRow></TableHead>
                                    <TableBody>{ticketTypes.map(t => (
                                        <TableRow key={t.id}>
                                            <TableCell>{t.id}</TableCell>
                                            <TableCell>{t.name}</TableCell>
                                            <TableCell>
                                                <IconButton size="small" color="error" onClick={() => handleDeleteTicketType(t.id)}>
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}</TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </TabPanel>
            </Paper>
        </Container>
    );
}

export default Setup;
